import React, { useCallback, useEffect, useRef, useState } from 'react';
import './WallPainter.css';

/* ══════════════════ Constants ══════════════════ */
const GRID_RES = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const MAX_UNDO = 40;
const MAP_GAME_UNITS = 16000;
const GAME_TO_GRID = GRID_RES / MAP_GAME_UNITS; // 0.032

/* ══════════════════ Layer definitions (bitmask) ══════════════════ */
const LAYERS = [
  { id: 'wall',            label: 'Walls',          bit: 1 << 0,  rgb: [220, 50, 50]   as const, priority: 10 },
  { id: 'bush',            label: 'Bushes',         bit: 1 << 1,  rgb: [50, 190, 50]   as const, priority: 4  },
  { id: 'turret',          label: 'Turrets',        bit: 1 << 2,  rgb: [255, 210, 20]  as const, priority: 9  },
  { id: 'inhibitor',       label: 'Inhibitors',     bit: 1 << 3,  rgb: [180, 60, 255]  as const, priority: 8  },
  { id: 'jungleCamp',      label: 'Jungle Camps',   bit: 1 << 4,  rgb: [255, 140, 20]  as const, priority: 6  },
  { id: 'nexus',           label: 'Nexus',          bit: 1 << 5,  rgb: [30, 200, 240]  as const, priority: 7  },
  { id: 'spawn',           label: 'Spawn / Base',   bit: 1 << 6,  rgb: [255, 100, 200] as const, priority: 5  },
  { id: 'minionSpawn',     label: 'Minion Spawn',   bit: 1 << 7,  rgb: [0, 255, 170]   as const, priority: 5  },
  { id: 'jungleCampRange', label: 'Camp Range',     bit: 1 << 8,  rgb: [200, 100, 40]  as const, priority: 2  },
  { id: 'river',           label: 'River',          bit: 1 << 9,  rgb: [60, 120, 255]  as const, priority: 1  },
  { id: 'scuttleArea',     label: 'Scuttle Area',   bit: 1 << 10, rgb: [100, 220, 255] as const, priority: 2  },
  { id: 'minionPath',      label: 'Minion Path',    bit: 1 << 11, rgb: [255, 255, 80]  as const, priority: 1  },
  { id: 'towerRange',      label: 'Tower Range',    bit: 1 << 12, rgb: [255, 60, 60]   as const, priority: 3  },
  { id: 'scuttleShrine',   label: 'Scuttle Shrine', bit: 1 << 13, rgb: [0, 255, 255]   as const, priority: 2  },
] as const;

type LayerDef = (typeof LAYERS)[number];

/** Layers sorted low-to-high priority for compositing (highest paints last = on top) */
const LAYERS_BY_PRIORITY = [...LAYERS].sort((a, b) => a.priority - b.priority);

/** Default circle radius in game units for certain layers */
const CIRCLE_DEFAULTS: Record<string, number> = {
  towerRange: 750,
  scuttleShrine: 500,
  jungleCampRange: 600,
};

/* ══════════════════ Helpers ══════════════════ */

function uint8ToBase64(arr: Uint8Array): string {
  let bin = '';
  const chunk = 8192;
  for (let i = 0; i < arr.length; i += chunk) {
    bin += String.fromCharCode(...arr.subarray(i, Math.min(i + chunk, arr.length)));
  }
  return btoa(bin);
}

function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

function uint16ToBase64(arr: Uint16Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  return uint8ToBase64(bytes);
}

function base64ToUint16(b64: string): Uint16Array {
  const bytes = base64ToUint8(b64);
  const aligned = new ArrayBuffer(bytes.length);
  new Uint8Array(aligned).set(bytes);
  return new Uint16Array(aligned);
}

/** Convert old v1 single-value grid to v2 bitmask grid */
function convertV1ToV2(old: Uint8Array): Uint16Array {
  const grid = new Uint16Array(old.length);
  for (let i = 0; i < old.length; i++) {
    const v = old[i];
    if (v >= 1 && v <= 14) grid[i] = 1 << (v - 1);
  }
  return grid;
}

/** Bresenham line */
function walkLine(
  x0: number, y0: number, x1: number, y1: number,
  cb: (x: number, y: number) => void,
) {
  let dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  while (true) {
    cb(x0, y0);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x0 += sx; }
    if (e2 < dx) { err += dx; y0 += sy; }
  }
}

/** Stamp a filled circle — sets or clears a single layer bit */
function stampCircle(
  grid: Uint16Array, cx: number, cy: number, r: number,
  bit: number, erase: boolean, modified: Set<number>,
) {
  const r2 = r * r;
  const ri = Math.ceil(r);
  for (let dy = -ri; dy <= ri; dy++) {
    const gy = cy + dy;
    if (gy < 0 || gy >= GRID_RES) continue;
    for (let dx = -ri; dx <= ri; dx++) {
      if (dx * dx + dy * dy > r2) continue;
      const gx = cx + dx;
      if (gx < 0 || gx >= GRID_RES) continue;
      const idx = gy * GRID_RES + gx;
      if (erase) {
        if (grid[idx] & bit) { grid[idx] &= ~bit; modified.add(idx); }
      } else {
        if (!(grid[idx] & bit)) { grid[idx] |= bit; modified.add(idx); }
      }
    }
  }
}

/** Flood-fill: sets or clears a single layer bit on contiguous matching cells */
function floodFill(
  grid: Uint16Array, sx: number, sy: number,
  bit: number, erase: boolean, modified: Set<number>,
) {
  const idx0 = sy * GRID_RES + sx;
  const hasBit = (grid[idx0] & bit) !== 0;

  if (erase) {
    if (!hasBit) return; // nothing to erase here
  } else {
    if (hasBit) return;  // already filled
  }

  const stack: number[] = [sx, sy];
  const visited = new Uint8Array(GRID_RES * GRID_RES);

  while (stack.length > 0) {
    const cy = stack.pop()!;
    const cx = stack.pop()!;
    if (cx < 0 || cx >= GRID_RES || cy < 0 || cy >= GRID_RES) continue;
    const ci = cy * GRID_RES + cx;
    if (visited[ci]) continue;
    visited[ci] = 1;

    const cellHas = (grid[ci] & bit) !== 0;
    if (erase ? !cellHas : cellHas) continue; // boundary

    if (erase) grid[ci] &= ~bit;
    else       grid[ci] |= bit;
    modified.add(ci);

    stack.push(cx + 1, cy);
    stack.push(cx - 1, cy);
    stack.push(cx, cy + 1);
    stack.push(cx, cy - 1);
  }
}

/* ══════════════════ Component ══════════════════ */

export default function WallPainter() {
  /* ── State ── */
  const [activeIdx, setActiveIdx]       = useState(0); // index into LAYERS
  const [tool, setTool]                 = useState<'brush' | 'eraser' | 'line' | 'fill' | 'circle'>('brush');
  const [brushSize, setBrushSize]       = useState(6);
  const [circleRadius, setCircleRadius] = useState(750); // game units
  const [overlayOpacity, setOverlayOpacity] = useState(0.55);
  const [layerVisibility, setLayerVisibility] = useState<Record<number, boolean>>(
    () => Object.fromEntries(LAYERS.map(l => [l.bit, true])),
  );

  /* ── Zoom / Pan ── */
  const [zoom, setZoom] = useState(1);
  const [pan, setPan]   = useState({ x: 0, y: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);

  /* ── Misc UI ── */
  const [stageW, setStageW]       = useState(800);
  const [stageH, setStageH]       = useState(800);
  const [undoLen, setUndoLen]     = useState(0);
  const [redoLen, setRedoLen]     = useState(0);
  const [renderTick, setRenderTick] = useState(0);
  const [cursorInfo, setCursorInfo] = useState({ gx: -1, gy: -1 });

  /* ── Refs ── */
  const gridRef      = useRef(new Uint16Array(GRID_RES * GRID_RES));
  const imgDataRef   = useRef<ImageData | null>(null);
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const viewportRef  = useRef<HTMLDivElement>(null);
  const cursorRef    = useRef<HTMLDivElement>(null);
  const paintingRef  = useRef(false);
  const panningRef   = useRef(false);
  const spaceRef     = useRef(false);
  const lastPanRef   = useRef({ x: 0, y: 0 });
  const lastGridRef  = useRef<{ gx: number; gy: number } | null>(null);
  const undoStack    = useRef<Uint16Array[]>([]);
  const redoStack    = useRef<Uint16Array[]>([]);

  const lineStartRef        = useRef<{ gx: number; gy: number } | null>(null);
  const lineGridSnapshotRef = useRef<Uint16Array | null>(null);

  const activeLayer = LAYERS[activeIdx];
  const activeBit   = activeLayer.bit;

  const sRef = useRef({ activeIdx, activeBit, tool, brushSize, circleRadius, overlayOpacity, layerVisibility, zoom, pan, stageW, stageH });
  useEffect(() => {
    sRef.current = { activeIdx, activeBit, tool, brushSize, circleRadius, overlayOpacity, layerVisibility, zoom, pan, stageW, stageH };
  });

  /* ═══════════ Viewport sizing ═══════════ */
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    setStageW(el.clientWidth);
    setStageH(el.clientHeight);
    const obs = new ResizeObserver(entries => {
      for (const e of entries) {
        setStageW(e.contentRect.width);
        setStageH(e.contentRect.height);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ═══════════ Full canvas render (composites all visible layers) ═══════════ */
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const grid = gridRef.current;
    const imgData = ctx.createImageData(GRID_RES, GRID_RES);
    const alpha = Math.round(overlayOpacity * 255);

    // Pre-filter visible layers sorted by priority (low → high)
    const vis = LAYERS_BY_PRIORITY.filter(l => layerVisibility[l.bit]);

    for (let i = 0; i < GRID_RES * GRID_RES; i++) {
      const mask = grid[i];
      if (mask === 0) continue;

      let r = 0, g = 0, b = 0, first = true;
      for (const layer of vis) {
        if (!(mask & layer.bit)) continue;
        if (first) {
          r = layer.rgb[0]; g = layer.rgb[1]; b = layer.rgb[2];
          first = false;
        } else {
          r = r * 0.3 + layer.rgb[0] * 0.7;
          g = g * 0.3 + layer.rgb[1] * 0.7;
          b = b * 0.3 + layer.rgb[2] * 0.7;
        }
      }
      if (first) continue; // no visible layers on this cell

      const off = i * 4;
      imgData.data[off]     = r;
      imgData.data[off + 1] = g;
      imgData.data[off + 2] = b;
      imgData.data[off + 3] = alpha;
    }
    ctx.putImageData(imgData, 0, 0);
    imgDataRef.current = imgData;
  }, [overlayOpacity, layerVisibility]);

  useEffect(() => { renderCanvas(); }, [renderCanvas, renderTick]);

  /* ═══════════ Incremental pixel patch ═══════════ */
  function patchPixels(modified: Set<number>) {
    const canvas = canvasRef.current;
    const imgData = imgDataRef.current;
    if (!canvas || !imgData) return;
    const ctx = canvas.getContext('2d')!;
    const grid = gridRef.current;
    const s = sRef.current;
    const alpha = Math.round(s.overlayOpacity * 255);

    const vis = LAYERS_BY_PRIORITY.filter(l => s.layerVisibility[l.bit]);

    let minX = GRID_RES, maxX = 0, minY = GRID_RES, maxY = 0;
    for (const idx of modified) {
      const gx = idx % GRID_RES;
      const gy = (idx - gx) / GRID_RES;
      if (gx < minX) minX = gx;
      if (gx > maxX) maxX = gx;
      if (gy < minY) minY = gy;
      if (gy > maxY) maxY = gy;

      const mask = grid[idx];
      const off = idx * 4;

      if (mask === 0) {
        imgData.data[off + 3] = 0;
        continue;
      }

      let r = 0, g = 0, b = 0, first = true;
      for (const layer of vis) {
        if (!(mask & layer.bit)) continue;
        if (first) {
          r = layer.rgb[0]; g = layer.rgb[1]; b = layer.rgb[2];
          first = false;
        } else {
          r = r * 0.3 + layer.rgb[0] * 0.7;
          g = g * 0.3 + layer.rgb[1] * 0.7;
          b = b * 0.3 + layer.rgb[2] * 0.7;
        }
      }
      if (first) {
        imgData.data[off + 3] = 0;
      } else {
        imgData.data[off]     = r;
        imgData.data[off + 1] = g;
        imgData.data[off + 2] = b;
        imgData.data[off + 3] = alpha;
      }
    }
    if (minX <= maxX && minY <= maxY) {
      ctx.putImageData(imgData, 0, 0, minX, minY, maxX - minX + 1, maxY - minY + 1);
    }
  }

  /* ═══════════ Imperative full render (for line preview) ═══════════ */
  function renderFull() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const grid = gridRef.current;
    const s = sRef.current;
    const imgData = ctx.createImageData(GRID_RES, GRID_RES);
    const alpha = Math.round(s.overlayOpacity * 255);

    const vis = LAYERS_BY_PRIORITY.filter(l => s.layerVisibility[l.bit]);

    for (let i = 0; i < GRID_RES * GRID_RES; i++) {
      const mask = grid[i];
      if (mask === 0) continue;
      let r = 0, g = 0, b = 0, first = true;
      for (const layer of vis) {
        if (!(mask & layer.bit)) continue;
        if (first) {
          r = layer.rgb[0]; g = layer.rgb[1]; b = layer.rgb[2];
          first = false;
        } else {
          r = r * 0.3 + layer.rgb[0] * 0.7;
          g = g * 0.3 + layer.rgb[1] * 0.7;
          b = b * 0.3 + layer.rgb[2] * 0.7;
        }
      }
      if (first) continue;
      const off = i * 4;
      imgData.data[off]     = r;
      imgData.data[off + 1] = g;
      imgData.data[off + 2] = b;
      imgData.data[off + 3] = alpha;
    }
    ctx.putImageData(imgData, 0, 0);
    imgDataRef.current = imgData;
  }

  /* ═══════════ Coordinate conversion ═══════════ */
  function screenToGrid(clientX: number, clientY: number): { gx: number; gy: number } | null {
    const vp = viewportRef.current;
    if (!vp) return null;
    const s = sRef.current;
    const rect = vp.getBoundingClientRect();
    const borderL = vp.clientLeft;
    const borderT = vp.clientTop;
    const vx = clientX - rect.left - borderL;
    const vy = clientY - rect.top  - borderT;
    const sx = (vx - s.pan.x) / s.zoom;
    const sy = (vy - s.pan.y) / s.zoom;
    const gx = Math.floor((sx / s.stageW) * GRID_RES);
    const gy = Math.floor((sy / s.stageH) * GRID_RES);
    if (gx < 0 || gx >= GRID_RES || gy < 0 || gy >= GRID_RES) return null;
    return { gx, gy };
  }

  /* ═══════════ Undo / Redo ═══════════ */
  function pushUndo() {
    undoStack.current.push(gridRef.current.slice());
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
    redoStack.current = [];
    setUndoLen(undoStack.current.length);
    setRedoLen(0);
  }
  function undo() {
    if (undoStack.current.length === 0) return;
    redoStack.current.push(gridRef.current.slice());
    gridRef.current = undoStack.current.pop()!;
    setUndoLen(undoStack.current.length);
    setRedoLen(redoStack.current.length);
    setRenderTick(n => n + 1);
  }
  function redo() {
    if (redoStack.current.length === 0) return;
    undoStack.current.push(gridRef.current.slice());
    gridRef.current = redoStack.current.pop()!;
    setUndoLen(undoStack.current.length);
    setRedoLen(redoStack.current.length);
    setRenderTick(n => n + 1);
  }

  /* ═══════════ Paint stroke logic ═══════════ */
  function doPaint(gx: number, gy: number) {
    const s = sRef.current;
    const erase = s.tool === 'eraser';
    const bit = LAYERS[s.activeIdx].bit;
    const modified = new Set<number>();
    const last = lastGridRef.current;

    if (last) {
      walkLine(last.gx, last.gy, gx, gy, (lx, ly) => {
        stampCircle(gridRef.current, lx, ly, s.brushSize, bit, erase, modified);
      });
    } else {
      stampCircle(gridRef.current, gx, gy, s.brushSize, bit, erase, modified);
    }
    lastGridRef.current = { gx, gy };
    if (modified.size > 0) patchPixels(modified);
  }

  /* ═══════════ Pointer handlers ═══════════ */
  function onPointerDown(e: React.PointerEvent) {
    const vp = viewportRef.current;
    if (!vp) return;

    // Pan
    if (e.button === 1 || (e.button === 0 && spaceRef.current)) {
      panningRef.current = true;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      vp.setPointerCapture(e.pointerId);
      return;
    }

    // Left-click
    if (e.button === 0) {
      const pos = screenToGrid(e.clientX, e.clientY);
      if (!pos) return;
      const s = sRef.current;
      const bit = LAYERS[s.activeIdx].bit;

      pushUndo();

      // Fill tool
      if (s.tool === 'fill') {
        const modified = new Set<number>();
        floodFill(gridRef.current, pos.gx, pos.gy, bit, false, modified);
        if (modified.size > 0) renderFull();
        return;
      }

      // Circle tool — single click stamps a circle at the game-unit radius
      if (s.tool === 'circle') {
        const radiusGrid = s.circleRadius * GAME_TO_GRID;
        const modified = new Set<number>();
        stampCircle(gridRef.current, pos.gx, pos.gy, radiusGrid, bit, false, modified);
        if (modified.size > 0) patchPixels(modified);
        return;
      }

      paintingRef.current = true;
      vp.setPointerCapture(e.pointerId);

      if (s.tool === 'line') {
        lineStartRef.current = pos;
        lineGridSnapshotRef.current = gridRef.current.slice();
      } else {
        lastGridRef.current = null;
        doPaint(pos.gx, pos.gy);
      }
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    // Brush cursor
    const vp = viewportRef.current;
    if (vp && cursorRef.current) {
      const rect = vp.getBoundingClientRect();
      const vx = e.clientX - rect.left;
      const vy = e.clientY - rect.top;
      const s = sRef.current;
      const cellPx = ((s.stageW + s.stageH) / 2) / GRID_RES;
      let displayRadius: number;
      if (s.tool === 'circle') {
        displayRadius = s.circleRadius * GAME_TO_GRID;
      } else {
        displayRadius = s.brushSize;
      }
      const displaySize = displayRadius * 2 * cellPx * s.zoom;
      const cur = cursorRef.current;
      cur.style.left   = `${vx}px`;
      cur.style.top    = `${vy}px`;
      cur.style.width  = `${displaySize}px`;
      cur.style.height = `${displaySize}px`;
      cur.style.display = 'block';
      cur.style.borderStyle = s.tool === 'eraser' ? 'dashed' : 'solid';
    }

    const gpos = screenToGrid(e.clientX, e.clientY);
    if (gpos) setCursorInfo(gpos);

    // Pan
    if (panningRef.current) {
      const dx = e.clientX - lastPanRef.current.x;
      const dy = e.clientY - lastPanRef.current.y;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
      return;
    }

    if (!paintingRef.current) return;
    const s = sRef.current;

    if (s.tool === 'line') {
      const start = lineStartRef.current;
      const snap = lineGridSnapshotRef.current;
      if (!start || !snap || !gpos) return;
      gridRef.current.set(snap);
      const bit = LAYERS[s.activeIdx].bit;
      const modified = new Set<number>();
      walkLine(start.gx, start.gy, gpos.gx, gpos.gy, (lx, ly) => {
        stampCircle(gridRef.current, lx, ly, s.brushSize, bit, false, modified);
      });
      renderFull();
    } else {
      const pos = screenToGrid(e.clientX, e.clientY);
      if (pos) doPaint(pos.gx, pos.gy);
    }
  }

  function onPointerUp(_e: React.PointerEvent) {
    if (paintingRef.current && sRef.current.tool === 'line') {
      lineStartRef.current = null;
      lineGridSnapshotRef.current = null;
      renderFull();
    }
    paintingRef.current = false;
    panningRef.current  = false;
    lastGridRef.current = null;
  }

  function onPointerLeave() {
    if (cursorRef.current) cursorRef.current.style.display = 'none';
  }

  /* ═══════════ Wheel zoom ═══════════ */
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      const s = sRef.current;
      const rect = vp!.getBoundingClientRect();
      const mx = e.clientX - rect.left - vp!.clientLeft;
      const my = e.clientY - rect.top  - vp!.clientTop;
      const sx = (mx - s.pan.x) / s.zoom;
      const sy = (my - s.pan.y) / s.zoom;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, s.zoom * factor));
      setZoom(newZoom);
      setPan({ x: mx - sx * newZoom, y: my - sy * newZoom });
    }
    vp.addEventListener('wheel', handleWheel, { passive: false });
    return () => vp.removeEventListener('wheel', handleWheel);
  }, []);

  /* ═══════════ Keyboard shortcuts ═══════════ */
  useEffect(() => {
    function down(e: KeyboardEvent) {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      const key = e.key.toLowerCase();

      if (e.key === ' ') { spaceRef.current = true; setSpaceHeld(true); e.preventDefault(); }
      if (key >= '1' && key <= '9') {
        const idx = parseInt(key) - 1;
        if (idx < LAYERS.length) setActiveIdx(idx);
      }
      if (key === '0' && LAYERS.length >= 10) setActiveIdx(9);
      if (key === 'b') setTool('brush');
      if (key === 'l') setTool('line');
      if (key === 'g') setTool('fill');
      if (key === 'c') setTool('circle');
      if (key === 'e') setTool('eraser');
      if (e.key === '[') setBrushSize(s => Math.max(1, s - 1));
      if (e.key === ']') setBrushSize(s => Math.min(50, s + 1));
      if (e.ctrlKey && !e.shiftKey && key === 'z') { e.preventDefault(); undo(); }
      if (e.ctrlKey && (key === 'y' || (e.shiftKey && key === 'z'))) { e.preventDefault(); redo(); }
    }
    function up(e: KeyboardEvent) {
      if (e.key === ' ') { spaceRef.current = false; setSpaceHeld(false); }
    }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ═══════════ Auto-set circle radius when switching layers ═══════════ */
  useEffect(() => {
    const def = CIRCLE_DEFAULTS[activeLayer.id];
    if (def) setCircleRadius(def);
  }, [activeLayer.id]);

  /* ═══════════ Export / Import ═══════════ */
  function exportGrid() {
    const grid = gridRef.current;

    // Stats: count cells where each bit is present
    const stats: Record<string, number> = { empty: 0 };
    for (const l of LAYERS) stats[l.id] = 0;
    for (let i = 0; i < grid.length; i++) {
      if (grid[i] === 0) { stats.empty++; continue; }
      for (const l of LAYERS) {
        if (grid[i] & l.bit) stats[l.id]++;
      }
    }

    const payload = {
      version: 2,
      gridResolution: GRID_RES,
      grid: uint16ToBase64(grid),
      layers: LAYERS.map(l => ({ id: l.id, bit: l.bit, label: l.label })),
      stats,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'walkability-grid.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  function importGrid() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string);
          if (data.gridResolution !== GRID_RES) {
            alert('Incompatible grid resolution');
            return;
          }
          pushUndo();
          if (data.version === 1) {
            // Old format: Uint8Array single-value → convert to bitmask
            const oldGrid = base64ToUint8(data.grid);
            gridRef.current = convertV1ToV2(oldGrid);
          } else if (data.version === 2) {
            gridRef.current = base64ToUint16(data.grid);
          } else {
            alert('Unknown version: ' + data.version);
            return;
          }
          setRenderTick(n => n + 1);
        } catch (err) {
          alert('Import failed: ' + err);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  function clearAll() {
    if (!window.confirm('Clear ALL painted data? This cannot be undone beyond the undo stack.')) return;
    pushUndo();
    gridRef.current = new Uint16Array(GRID_RES * GRID_RES);
    setRenderTick(n => n + 1);
  }

  function toggleLayer(bit: number) {
    setLayerVisibility(prev => ({ ...prev, [bit]: !prev[bit] }));
  }

  function resetView() { setZoom(1); setPan({ x: 0, y: 0 }); }

  function selectLayer(idx: number) {
    setActiveIdx(idx);
    // Auto-switch to circle tool for range layers
    const id = LAYERS[idx].id;
    if (CIRCLE_DEFAULTS[id] && tool !== 'eraser') {
      setTool('circle');
    }
  }

  /* ═══════════ Render ═══════════ */
  return (
    <div className="wp-container">
      {/* ── Toolbar ── */}
      <div className="wp-toolbar">
        {/* Row 1 — Tool, Brush Size, Circle Radius, Opacity */}
        <div className="wp-row">
          <div className="wp-group">
            <span className="wp-label">Tool</span>
            <div className="wp-btnset">
              <button className={`wp-btn ${tool === 'brush' ? 'active' : ''}`} onClick={() => setTool('brush')}>
                Brush (B)
              </button>
              <button className={`wp-btn ${tool === 'line' ? 'active' : ''}`} onClick={() => setTool('line')}>
                Line (L)
              </button>
              <button className={`wp-btn ${tool === 'fill' ? 'active' : ''}`} onClick={() => setTool('fill')}>
                Fill (G)
              </button>
              <button className={`wp-btn ${tool === 'circle' ? 'active' : ''}`} onClick={() => setTool('circle')}>
                Circle (C)
              </button>
              <button className={`wp-btn ${tool === 'eraser' ? 'active' : ''}`} onClick={() => setTool('eraser')}>
                Eraser (E)
              </button>
            </div>
          </div>

          {tool !== 'circle' && (
            <div className="wp-group">
              <span className="wp-label">Size: {brushSize}</span>
              <input
                type="range" min={1} max={40} value={brushSize}
                onChange={e => setBrushSize(Number(e.target.value))}
                className="wp-slider"
              />
            </div>
          )}

          {tool === 'circle' && (
            <div className="wp-group">
              <span className="wp-label">Radius:</span>
              <input
                type="number" min={50} max={3000} step={25} value={circleRadius}
                onChange={e => setCircleRadius(Number(e.target.value))}
                className="wp-number-input"
              />
              <span className="wp-label wp-unit">units ({Math.round(circleRadius * GAME_TO_GRID)} cells)</span>
            </div>
          )}

          <div className="wp-group">
            <span className="wp-label">Overlay: {Math.round(overlayOpacity * 100)}%</span>
            <input
              type="range" min={5} max={100} value={Math.round(overlayOpacity * 100)}
              onChange={e => setOverlayOpacity(Number(e.target.value) / 100)}
              className="wp-slider"
            />
          </div>
        </div>

        {/* Row 2 — Layer selection */}
        <div className="wp-row">
          <div className="wp-group">
            <span className="wp-label">Layer</span>
            <div className="wp-btnset wp-layer-btns-wrap">
              {LAYERS.map((l, idx) => (
                <button
                  key={l.bit}
                  className={`wp-btn wp-layer-btn ${activeIdx === idx ? 'active' : ''}`}
                  onClick={() => selectLayer(idx)}
                  style={{ '--layer-color': `rgb(${l.rgb.join(',')})` } as React.CSSProperties}
                >
                  <span className="wp-layer-dot" />
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3 — Visibility toggles + Actions */}
        <div className="wp-row">
          <div className="wp-group">
            <span className="wp-label">Vis</span>
            <div className="wp-btnset wp-layer-btns-wrap">
              {LAYERS.map(l => (
                <label key={l.bit} className="wp-vis-toggle">
                  <input
                    type="checkbox"
                    checked={layerVisibility[l.bit]}
                    onChange={() => toggleLayer(l.bit)}
                  />
                  <span
                    className="wp-vis-dot"
                    style={{ background: layerVisibility[l.bit] ? `rgb(${l.rgb.join(',')})` : '#444' }}
                  />
                  <span className="wp-vis-text">{l.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="wp-group wp-actions">
            <div className="wp-btnset">
              <button className="wp-btn" onClick={undo} disabled={undoLen === 0}>Undo ({undoLen})</button>
              <button className="wp-btn" onClick={redo} disabled={redoLen === 0}>Redo ({redoLen})</button>
              <button className="wp-btn wp-export" onClick={exportGrid}>Export JSON</button>
              <button className="wp-btn" onClick={importGrid}>Import</button>
              <button className="wp-btn wp-danger" onClick={clearAll}>Clear All</button>
            </div>
          </div>
        </div>

        <div className="wp-hint">
          Scroll to zoom &middot; Space+drag to pan &middot; <b>[ ]</b> brush size &middot; <b>1-9, 0</b> select layer &middot;
          <b>B</b> brush &middot; <b>L</b> line &middot; <b>G</b> fill &middot; <b>C</b> circle &middot; <b>E</b> eraser &middot;
          <b>Ctrl+Z</b> undo &middot; <b>Ctrl+Y</b> redo
          {tool === 'circle' && <span> &middot; <em>Click to stamp circle at {circleRadius} unit radius</em></span>}
        </div>
      </div>

      {/* ── Map Viewport ── */}
      <div
        ref={viewportRef}
        className={`wp-viewport${spaceHeld ? ' pan-mode' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onContextMenu={e => e.preventDefault()}
      >
        <div
          className="wp-stage"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          <img className="wp-map" src="/Images/riftmap.jpg" alt="Summoner's Rift" draggable={false} />
          <canvas
            ref={canvasRef}
            className="wp-canvas"
            width={GRID_RES}
            height={GRID_RES}
          />
        </div>
        <div ref={cursorRef} className="wp-brush-cursor" style={{ display: 'none' }} />
      </div>

      {/* ── Status Bar ── */}
      <div className="wp-status">
        <span>Zoom: <b>{zoom.toFixed(1)}x</b></span>
        <span>Grid: <b>({cursorInfo.gx >= 0 ? cursorInfo.gx : '—'}, {cursorInfo.gy >= 0 ? cursorInfo.gy : '—'})</b></span>
        <span>
          Layer: <b style={{ color: `rgb(${activeLayer.rgb.join(',')})` }}>
            {activeLayer.label}
          </b>
        </span>
        <span>Tool: <b>{tool}</b></span>
        {tool === 'circle' && <span>Radius: <b>{circleRadius} units</b></span>}
        <span>Res: <b>{GRID_RES}×{GRID_RES}</b></span>
        <button className="wp-btn wp-btn-sm" onClick={resetView}>Reset View</button>
      </div>
    </div>
  );
}
